import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { DataReferenceMatch, DataReferenceMatchLocation, DataReferenceMatches } from '../api/client'

/** What the inline hint (ADR-179 P1) and the sweep panel (ADR-179 P2) both read. */
export interface MatchesState {
  matches: DataReferenceMatches | null
  /** Why there is nothing to show, when the reason is that we could not look. Never set
   *  alongside `matches`: the two answers must not be readable as one. */
  unavailableNote: string | null
  /** Ask again now, skipping the debounce — the panel does this when it opens. */
  refetch: () => void
  lookup: (location: DataReferenceMatchLocation) => DataReferenceMatch | null
}

const DEBOUNCE_MS = 500

/** Same location, by value. The address is a tuple, so identity is not available: the panel
 *  and the affordance both build their own objects from the draft they are rendering. */
export function sameLocation(
  a: DataReferenceMatchLocation, b: DataReferenceMatchLocation,
): boolean {
  return a.task === b.task && a.slot === b.slot && a.index === b.index && a.key === b.key
}

/**
 * One matches call per draft edit, shared by P1 and P2 (ADR-179).
 *
 * The response is kept only when it echoes the CURRENT revision. The counter lives here and
 * is sent with the request; the endpoint hands it back verbatim. Hashing the draft in the
 * browser would be the other way to tell a reply apart, and it is not available — `crypto.subtle`
 * is absent outside secure contexts, and nothing in this app uses it.
 *
 * A `note` on the response and a transport failure are the same fact to a reader — the matcher
 * could not be asked — so both clear `matches` and set `unavailableNote`. An empty `locations`
 * without a note is the opposite fact and is kept as a result.
 */
export function useMatches(draftYAML: string | null | undefined): MatchesState {
  const [matches, setMatches] = useState<DataReferenceMatches | null>(null)
  const [unavailableNote, setUnavailableNote] = useState<string | null>(null)
  const revisionRef = useRef(0)
  const [nonce, setNonce] = useState(0)
  /** Set by refetch, so that one pass skips the debounce and only that one. */
  const immediateRef = useRef(false)
  /** The draft the state in `matches` answers about, so a refetch of an unchanged draft is
   *  told apart from an edit. */
  const lastDraftRef = useRef<string | null>(null)

  useEffect(() => {
    // The previous answer is about the previous draft, and this one has changed: keeping it
    // for the debounce window would render P1 hints and the P2 badge derived from a document
    // that is no longer on screen — a hint whose "this literal is published" claim names the
    // field beside it is the one thing ADR-179 P1 promises is exact. Cleared here rather than
    // on arrival, and the revision is bumped FIRST so an in-flight reply for the old draft
    // cannot re-populate what this run just cleared — including in the no-draft branch, which
    // used to return without bumping and so left a stale reply free to land.
    // A refetch of the SAME draft is not a stale answer, so it keeps what is on screen —
    // only an edit invalidates it.
    const revision = String(++revisionRef.current)
    if (draftYAML !== lastDraftRef.current) {
      lastDraftRef.current = draftYAML ?? null
      setMatches(null)
    }
    if (!draftYAML) {
      setUnavailableNote(null)
      return
    }
    const immediate = immediateRef.current
    immediateRef.current = false

    /** Either way the matcher could not be asked, the editor keeps working: this is an
     *  advisory pass over the draft, not part of saving one. */
    const unavailable = (e: unknown) => {
      if (revision !== String(revisionRef.current)) return
      setMatches(null)
      setUnavailableNote(
        `Could not check this draft for hardcoded values: ${e instanceof Error ? e.message : String(e)}`)
    }

    const fire = () => {
      let pending: Promise<DataReferenceMatches>
      try {
        pending = api.dataReferenceMatches(draftYAML, revision)
      } catch (e) {
        // A call that throws before it returns a promise — the hook fires from a timer, so
        // an unhandled one would surface as an uncaught exception rather than a note.
        unavailable(e)
        return
      }
      pending
        .then(response => {
          // A reply about a draft that has since been edited is not an answer about this one.
          if (response.revision !== String(revisionRef.current)) return
          if (response.note) {
            setMatches(null)
            setUnavailableNote(response.note)
            return
          }
          setMatches(response)
          setUnavailableNote(null)
        })
        .catch(unavailable)
    }

    if (immediate) {
      fire()
      return
    }
    const timer = setTimeout(fire, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draftYAML, nonce])

  const refetch = useCallback(() => {
    immediateRef.current = true
    setNonce(n => n + 1)
  }, [])

  const lookup = useCallback((location: DataReferenceMatchLocation) =>
    matches?.locations.find(m => sameLocation(m.location, location)) ?? null, [matches])

  return { matches, unavailableNote, refetch, lookup }
}
